import { useEffect, useMemo, useRef, useState } from "react";

import { ChatMessageAttachment } from "../../api/services/chat";
import { Icon } from "../../components/Icon";

import {
  FALLBACK_WAVEFORM_BARS,
  formatVoiceTime,
  ratioFromPointer,
  resampleWaveform,
} from "./chatUtils";
import { voicePlayback } from "./voicePlaybackController";

interface VoiceMessageBubbleProps {
  attachment: ChatMessageAttachment;
  isOwnMessage: boolean;
}

export default function VoiceMessageBubble({
  attachment,
  isOwnMessage,
}: VoiceMessageBubbleProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveformContainerRef = useRef<HTMLDivElement | null>(null);
  const [barSlots, setBarSlots] = useState(40);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(
    attachment.audioDurationMs ? attachment.audioDurationMs / 1000 : 0,
  );
  const [dragProgress, setDragProgress] = useState<number | null>(null);
  const [isRecordingElsewhere, setIsRecordingElsewhere] = useState(
    voicePlayback.isRecording(),
  );

  useEffect(() => {
    const unsubscribe = voicePlayback.subscribe(() => {
      setIsRecordingElsewhere(voicePlayback.isRecording());
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const sourceBars = useMemo(() => {
    if (attachment.audioWaveform && attachment.audioWaveform.length > 0) {
      return attachment.audioWaveform;
    }
    return FALLBACK_WAVEFORM_BARS;
  }, [attachment.audioWaveform]);

  const bars = useMemo(
    () => resampleWaveform(sourceBars, Math.min(barSlots, 40)),
    [sourceBars, barSlots],
  );

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "metadata";
      audioRef.current.src = attachment.url;
    }
    const audio = audioRef.current;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };
    const onEnded = () => {
      voicePlayback.release(audio);
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onPause = () => {
      if (!voicePlayback.isCurrent(audio)) setIsPlaying(false);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("durationchange", onLoaded);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("pause", onPause);

    const unsubscribe = voicePlayback.subscribe(() => {
      if (!voicePlayback.isCurrent(audio) && !audio.paused) {
        audio.pause();
        setIsPlaying(false);
      }
    });

    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("durationchange", onLoaded);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("pause", onPause);
      unsubscribe();
      audio.pause();
      voicePlayback.release(audio);
    };
  }, [attachment.url]);

  useEffect(() => {
    const el = waveformContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const compute = () => {
      const width = el.clientWidth;
      const slots = Math.max(1, Math.min(40, Math.floor(width / 4)));
      setBarSlots(slots);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const togglePlay = () => {
    if (voicePlayback.isRecording()) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      voicePlayback.claim(audio);
      audio.play().catch(() => undefined);
      setIsPlaying(true);
    } else {
      audio.pause();
      voicePlayback.release(audio);
      setIsPlaying(false);
    }
  };

  const seekToRatio = (ratio: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const clamped = Math.min(1, Math.max(0, ratio));
    const target = clamped * duration;
    if (Number.isFinite(target)) {
      audio.currentTime = target;
      setCurrentTime(target);
    }
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (voicePlayback.isRecording()) return;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const ratio = ratioFromPointer(e.clientX, el.getBoundingClientRect());
    setDragProgress(ratio);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragProgress === null) return;
    const ratio = ratioFromPointer(
      e.clientX,
      e.currentTarget.getBoundingClientRect(),
    );
    setDragProgress(ratio);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
    if (dragProgress !== null) {
      seekToRatio(dragProgress);
      setDragProgress(null);
    }
  };

  const liveProgress = duration > 0 ? currentTime / duration : 0;
  const progress = dragProgress ?? liveProgress;
  const displayTime = isPlaying || currentTime > 0 ? currentTime : duration;

  const playButtonClasses = isOwnMessage ? "text-white" : "text-[#1E293B]";
  const activeBarColor = isOwnMessage ? "bg-white" : "bg-[#1E293B]";
  const inactiveBarColor = isOwnMessage ? "bg-white/30" : "bg-[#1E293B]/20";
  const timeColor = isOwnMessage ? "text-white/70" : "text-[#1E293B]/70";

  return (
    <div className="flex items-center gap-2 w-[220px] lg:w-[240px]">
      <button
        type="button"
        onClick={togglePlay}
        disabled={isRecordingElsewhere}
        className={`w-[30px] h-[30px] flex-shrink-0 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed ${playButtonClasses}`}
        aria-label={isPlaying ? "Pause voice message" : "Play voice message"}
      >
        <Icon
          name={isPlaying ? "pause" : "play"}
          className="w-[18px] h-[18px]"
        />
      </button>

      <div
        ref={waveformContainerRef}
        className="flex-1 flex items-center h-7 touch-none cursor-pointer select-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {bars.map((amp, i) => {
          const height = Math.max(3, (amp / 100) * 25 + 3);
          const activeCount = Math.floor(progress * bars.length);
          const isActive = i < activeCount;
          return (
            <div
              key={i}
              className={`rounded-full flex-shrink-0 ${
                isActive ? activeBarColor : inactiveBarColor
              }`}
              style={{
                height: `${height}px`,
                marginRight: "1.5px",
                width: "2.5px",
              }}
            />
          );
        })}
      </div>

      <span
        className={`text-[11px] lg:text-xs font-medium tabular-nums flex-shrink-0 ${timeColor}`}
      >
        {formatVoiceTime(displayTime)}
      </span>
    </div>
  );
}
